"use client"

// src/components/auth/verificacion/Verification.tsx
import type React from "react"
import { useState, useEffect } from "react"
import { api } from "../../../services/Axios"
import { useNavigate } from "react-router-dom"
import Loader from "../../utils/loader/Loader"
import MessageModal from "../../utils/avisos/MessageModal"
import { useAuth } from "../../../services/AuthContext"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faCheckCircle, faEnvelope, faShieldAlt } from "@fortawesome/free-solid-svg-icons"

const Verification: React.FC = () => {
  const [email, setEmail] = useState("")
  const [code, setCode] = useState<string[]>(["", "", "", "", "", ""])
  const [timer, setTimer] = useState(0)
  const [isLoading, setIsLoading] = useState(false)
  const [modal, setModal] = useState<{ isOpen: boolean; type: "success" | "error"; message: string } | null>(null)
  const [redirectAfterSuccess, setRedirectAfterSuccess] = useState(false)
  const navigate = useNavigate()
  const { syncAuthStatus } = useAuth()

  useEffect(() => {
    const storedEmail = localStorage.getItem("email")
    if (storedEmail) setEmail(storedEmail)
  }, [])

  useEffect(() => {
    if (timer > 0) {
      const interval = setInterval(() => setTimer((prev) => prev - 1), 1000)
      return () => clearInterval(interval)
    }
  }, [timer])

  const handleInputChange = (value: string, index: number) => {
    if (/^\d*$/.test(value)) {
      const newCode = [...code]
      newCode[index] = value
      setCode(newCode)

      if (value && index < 5) {
        document.getElementById(`code-input-${index + 1}`)?.focus()
      }
    }
  }

  const handleVerifyCode = async (e: React.FormEvent) => {
    e.preventDefault()
    const verificationCode = code.join("")
    setIsLoading(true)
    try {
      const response = await api.post("/api/auth/verify", { email, code: verificationCode })
      localStorage.setItem("token", response.data.token)
      setModal({ isOpen: true, type: "success", message: "¡Cuenta verificada exitosamente!" })
      setRedirectAfterSuccess(true)
      await syncAuthStatus()
    } catch {
      setModal({ isOpen: true, type: "error", message: "Código incorrecto o expirado" })
    } finally {
      setIsLoading(false)
    }
  }

  const handleCloseModal = () => {
    if (redirectAfterSuccess) navigate("/")
    setModal(null)
  }

  const handleResendCode = async () => {
    setIsLoading(true)
    try {
      await api.post("/api/auth/resend-code", { email })
      setModal({ isOpen: true, type: "success", message: "Nuevo código enviado al correo." })
      setTimer(300)
      setRedirectAfterSuccess(false)
    } catch {
      setModal({ isOpen: true, type: "error", message: "Error al reenviar el código." })
    } finally {
      setIsLoading(false)
    }
  }

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${mins}:${secs < 10 ? "0" : ""}${secs}`
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
      {isLoading && <Loader />}
      <MessageModal
        isOpen={modal?.isOpen || false}
        message={modal?.message || ""}
        messageType={modal?.type || "info"}
        onClose={handleCloseModal}
      />

      <div className="w-full max-w-md bg-white rounded-2xl shadow-xl overflow-hidden transition-all duration-300 transform hover:shadow-2xl">
        <div className="bg-primary p-8 text-white text-center relative overflow-hidden">
          {/* Decorative elements */}
          <div className="absolute top-0 left-0 w-full h-full">
            <div className="absolute -top-10 -left-10 w-40 h-40 bg-white/10 rounded-full"></div>
            <div className="absolute top-20 -right-10 w-40 h-40 bg-white/10 rounded-full"></div>
          </div>

          <div className="relative z-10">
            <img src="/logos/logol.png" alt="Logo" className="h-20 mx-auto mb-4" />
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-white/20 mb-4">
              <FontAwesomeIcon icon={faShieldAlt} className="text-white text-3xl" />
            </div>
            <h2 className="text-2xl font-bold">Verificación de Cuenta</h2>
            <p className="mt-2 opacity-90 flex items-center justify-center">
              <FontAwesomeIcon icon={faEnvelope} className="mr-2" />
              Hemos enviado un código a {email}
            </p>
          </div>
        </div>

        <div className="p-8">
          <form onSubmit={handleVerifyCode} className="space-y-6">
            <div className="grid grid-cols-6 gap-2 justify-center">
              {code.map((digit, index) => (
                <input
                  key={index}
                  id={`code-input-${index}`}
                  type="text"
                  maxLength={1}
                  value={digit}
                  onChange={(e) => handleInputChange(e.target.value, index)}
                  className="w-12 h-14 text-center text-xl font-bold border-2 border-gray-300 rounded-lg focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all duration-200"
                />
              ))}
            </div>

            <button
              type="submit"
              className="w-full py-3 px-4 bg-primary hover:bg-primary/90 text-white font-medium rounded-lg shadow-md hover:shadow-lg transform hover:-translate-y-0.5 transition-all duration-200 flex items-center justify-center"
            >
              <FontAwesomeIcon icon={faCheckCircle} className="mr-2" /> Verificar Código
            </button>
          </form>

          <div className="mt-6 text-center">
            <button
              onClick={handleResendCode}
              disabled={timer > 0}
              className={`text-sm font-medium ${timer > 0 ? "text-gray-400" : "text-primary hover:text-primary/80"} transition-colors duration-200`}
            >
              {timer > 0 ? (
                <span>Reenviar código en {formatTime(timer)}</span>
              ) : (
                <span>¿No recibiste el código? Haz clic para reenviar</span>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Verification

